import React, { ReactNode, useCallback } from 'react';
import { isGridLayout, Offer, SelectedProduct } from '../../types';
import { useProductsContext } from '../../contexts/ProductsContext';
import { usePopupContext } from '../../contexts/Settings';
import { OfferBodyGrid } from './grid';
import { OfferBodyTree } from './tree';
import { logInfo } from '../../shared/utils/logging';
import { productHandleForVariant } from '../../shared/api/candyrack';
import { useSharedContext } from '../../contexts/SharedContext';

interface Props {
  offer: Offer;
  indexInOffers?: number;
  variantID?: number;
  cyData: string;
  upsellListItemCustomClassName?: string;
  img: ReactNode;
  animatedIcon: ReactNode | null;
  loading: boolean;
  added: boolean;
  select?: (className: string) => ReactNode | null;
  primaryButton: ReactNode;
  quantity: number;
  label?: string;
  handleRemoveFromCartClick: () => Promise<void>;
  setOfferNote: (note: string) => void;
  offerNote?: string;
  offerDescriptionHeadingTitle: ReactNode;
  offerDescriptionHeadingPrice: ReactNode;
  offerCount: number;
  handleOfferQuantityChange: React.Dispatch<React.SetStateAction<number>>;
  hidden?: boolean;

  // TODO: Tech debt. product.id for products, offer.id for services
  // Split this, use clear naming concepts
  id: number;
  handle?: string;
}

export const OfferBody = ({
  offer,
  indexInOffers,
  cyData,
  img,
  animatedIcon,
  loading,
  added,
  select,
  handleRemoveFromCartClick,
  primaryButton,
  setOfferNote,
  offerNote = '',
  offerDescriptionHeadingTitle,
  offerDescriptionHeadingPrice,
  quantity,
  offerCount,
  handleOfferQuantityChange,
  upsellListItemCustomClassName = '',
  hidden,
  id,
  variantID,
  handle,
}: Props) => {
  const { setSelectedProduct } = useProductsContext();
  const { settings } = usePopupContext();
  const { runningAppName } = useSharedContext();

  const openDetail = useCallback(async () => {
    const selectedProduct = {
      // TODO: Tech debt, id contains offer.id for services, setSelectedProduct is used for services.
      // Use clear naming concepts
      productID: id,
      variantID,
      offerID: offer.id,
    } as SelectedProduct;

    if (window.CANDYRACK_SHOULD_REDIRECT_ON_PRODUCT_DETAIL_CLICK) {
      let redirectUrl = '';
      if (
        cyData === 'offer-service' &&
        offer.items.length === 1 &&
        offer.items[0].item_variants.length === 1
      ) {
        const variantQueryParam = `?variant=${offer.items[0].item_variants[0]}`;
        // as its a service, we know we have exactly one variant
        const productHandleForService = await productHandleForVariant(
          offer.items[0].item_variants[0],
        );
        redirectUrl = `/products/${productHandleForService}${variantQueryParam}`;
      } else {
        const variantQueryParam = variantID ? `?variant=${variantID}` : '';
        redirectUrl = `/products/${handle}${variantQueryParam}`;
      }
      logInfo(`Redirecting to: ${redirectUrl}`, runningAppName);
      window.location.href = redirectUrl;
    } else {
      setSelectedProduct(selectedProduct);
    }
  }, [
    offer,
    id,
    setSelectedProduct,
    variantID,
    handle,
    runningAppName,
    cyData,
  ]);

  return isGridLayout(settings) ? (
    <OfferBodyGrid
      id={id}
      hidden={hidden}
      offer={offer}
      cyData={cyData}
      img={img}
      animatedIcon={animatedIcon}
      loading={loading}
      added={added}
      select={select}
      handleRemoveFromCartClick={handleRemoveFromCartClick}
      primaryButton={primaryButton}
      setOfferNote={setOfferNote}
      offerNote={offerNote}
      quantity={quantity}
      handleOfferQuantityChange={handleOfferQuantityChange}
      openDetail={openDetail}
      upsellListItemCustomClassName={upsellListItemCustomClassName}
      offerDescriptionHeadingTitle={offerDescriptionHeadingTitle}
      offerDescriptionHeadingPrice={offerDescriptionHeadingPrice}
    />
  ) : (
    <OfferBodyTree
      id={id}
      indexInOffers={indexInOffers}
      hidden={hidden}
      offer={offer}
      offerCount={offerCount}
      cyData={cyData}
      img={img}
      animatedIcon={animatedIcon}
      loading={loading}
      added={added}
      select={select}
      handleRemoveFromCartClick={handleRemoveFromCartClick}
      primaryButton={primaryButton}
      setOfferNote={setOfferNote}
      offerNote={offerNote}
      offerDescriptionHeadingTitle={offerDescriptionHeadingTitle}
      offerDescriptionHeadingPrice={offerDescriptionHeadingPrice}
      quantity={quantity}
      handleOfferQuantityChange={handleOfferQuantityChange}
      openDetail={openDetail}
      upsellListItemCustomClassName={upsellListItemCustomClassName}
    />
  );
};
